/**
 * F43 (Track B) — bulk-action drawer (idempotency-aware progress + partial-
 * success list + downloadable results CSV).
 *
 * Reused by the user list (`suspend`) and the report queue (`dismiss-reports`,
 * `reassign-reports`). A stable `Idempotency-Key` is minted once per open so a
 * retry replays the same `bulk_action_records` row server-side. After the call
 * resolves, the per-item `{targetId, status, reason?}` results render with a
 * summary line + a Download-CSV affordance. >100 targets surfaces
 * `BULK_LIMIT_EXCEEDED`.
 */
import { useEffect, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Badge, Banner, Button, Drawer, FormField, Select, Textarea } from '@bravemove/ui-web';
import {
  BULK_ACTION_MAX_TARGETS,
  SUSPENSION_REASON_MAX,
  type BulkActionResponse,
  type UserBulkActionResult,
} from '@bravemove/types';
import { reportsApi, type BulkActionKind } from '../../api/reports';
import { translateApiError } from '../../auth/errors.js';

/**
 * `reportsApi.BulkActionKind` is the server-bound report union; the user-list
 * bulk-DELETE flow extends it locally with `'delete'` (which never hits
 * `reportsApi.bulk` — it always rides a `presetResult`, see below) so the page
 * can title the shared results view without touching `reports.ts`.
 */
export type BulkKind = BulkActionKind | 'delete';

export interface BulkActionsDrawerProps {
  readonly open: boolean;
  readonly kind: BulkKind;
  readonly targetIds: readonly string[];
  /** Required only for `reassign-reports` — the admin id to assign to. */
  readonly assignableAdmins?: readonly { readonly id: string; readonly label: string }[];
  /**
   * When supplied, the drawer skips its own submit form and renders THIS result
   * directly (the shared results + CSV view). Used by the user-list bulk-DELETE
   * flow, whose confirm + dispatch happen in a `SuspendDialog` upstream so the
   * dedicated `users:delete` route (not `reportsApi.bulk`) is hit.
   */
  readonly presetResult?: BulkActionResponse | null;
  readonly onClose: () => void;
  readonly onDone: () => void;
}

function mintKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `bulk-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function buildCsv(results: readonly UserBulkActionResult[]): string {
  const header = 'targetId,status,reason';
  const lines = results.map(
    (r) => `${r.targetId},${r.status},${(r.reason ?? '').replace(/[\n,]/g, ' ')}`,
  );
  return [header, ...lines].join('\n');
}

export function BulkActionsDrawer({
  open,
  kind,
  targetIds,
  assignableAdmins = [],
  presetResult = null,
  onClose,
  onDone,
}: BulkActionsDrawerProps) {
  const { t } = useI18n();
  const [reason, setReason] = useState('');
  const [assignedAdminId, setAssignedAdminId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BulkActionResponse | null>(null);
  const [idempotencyKey, setIdempotencyKey] = useState('');

  /* eslint-disable react-hooks/set-state-in-effect -- reset transient state when re-opened */
  useEffect(() => {
    if (!open) return;
    setReason('');
    setAssignedAdminId('');
    setSubmitting(false);
    setError(null);
    // A preset result (the bulk-DELETE flow) renders the results view immediately.
    setResult(presetResult);
    setIdempotencyKey(mintKey());
  }, [open, presetResult]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const tooMany = targetIds.length > BULK_ACTION_MAX_TARGETS;
  const trimmed = reason.trim();
  const needsAssignee = kind === 'reassign-reports';
  const reasonValid = trimmed.length >= 1;
  const assigneeValid = !needsAssignee || assignedAdminId !== '';
  const canSubmit = !tooMany && reasonValid && assigneeValid && targetIds.length > 0;

  const title =
    kind === 'suspend'
      ? t('moderation:bulk.suspend')
      : kind === 'dismiss-reports'
        ? t('moderation:bulk.dismiss_reports')
        : kind === 'reassign-reports'
          ? t('moderation:bulk.reassign_reports')
          : t('admin:f43.bulk.delete_title', { defaultValue: 'Delete users' });

  const submit = async () => {
    // `kind === 'delete'` never reaches the submit form — it always rides a
    // `presetResult` (the SuspendDialog upstream hits the dedicated route). The
    // guard also narrows `kind` to the server-bound `BulkActionKind` for the
    // `reportsApi.bulk` call below.
    if (!canSubmit || kind === 'delete') return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await reportsApi.bulk(
        kind,
        {
          targetIds: [...targetIds],
          reason: trimmed,
          ...(needsAssignee ? { assignedAdminId } : {}),
        },
        idempotencyKey,
      );
      setResult(res);
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setSubmitting(false);
    }
  };

  const downloadCsv = () => {
    if (!result) return;
    const blob = new Blob([buildCsv(result.results)], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bulk-${kind}-results.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const resultTone = (s: UserBulkActionResult['status']): 'success' | 'danger' | 'warning' =>
    s === 'ok' ? 'success' : s === 'failed' ? 'danger' : 'warning';

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={title}
      footer={
        result === null ? (
          <>
            <Button variant="ghost" onClick={onClose} disabled={submitting}>
              {t('admin:f43.bulk.clear', { defaultValue: 'Cancel' })}
            </Button>
            <Button
              variant="primary"
              onClick={() => void submit()}
              loading={submitting}
              disabled={!canSubmit}
              data-testid="f43-bulk-submit"
            >
              {title}
            </Button>
          </>
        ) : (
          <Button variant="primary" onClick={onDone} data-testid="f43-bulk-done">
            {t('admin:f43.bulk.done', { defaultValue: 'Done' })}
          </Button>
        )
      }
    >
      <div className="space-y-4" data-testid="f43-bulk-drawer">
        {tooMany ? (
          <div data-testid="f43-bulk-limit-banner">
            <Banner tone="danger">{t('moderation:errors.BULK_LIMIT_EXCEEDED')}</Banner>
          </div>
        ) : null}

        {result === null ? (
          <>
            <p className="text-[13px] text-ink-2">
              {`Up to ${BULK_ACTION_MAX_TARGETS} items per action.`}
            </p>
            <p className="text-[13px] font-medium text-ink" data-testid="f43-bulk-target-count">
              {t('admin:f43.bulk.selected', { count: targetIds.length })}
            </p>
            {needsAssignee ? (
              <FormField label={t('moderation:bulk.assignee_label')}>
                <Select
                  value={assignedAdminId}
                  onChange={(e) => setAssignedAdminId(e.target.value)}
                  options={[
                    { value: '', label: '—' },
                    ...assignableAdmins.map((a) => ({ value: a.id, label: a.label })),
                  ]}
                  data-testid="f43-bulk-assignee"
                />
              </FormField>
            ) : null}
            <FormField label={t('moderation:bulk.reason_label')}>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={SUSPENSION_REASON_MAX}
                placeholder={t('moderation:bulk.reason_placeholder')}
                rows={3}
                data-testid="f43-bulk-reason"
              />
            </FormField>
            {error ? <Banner tone="danger">{error}</Banner> : null}
          </>
        ) : (
          <div className="space-y-3" data-testid="f43-bulk-results">
            <div data-testid="f43-bulk-summary">
              <Banner tone="info">
                {t('moderation:bulk.summary', {
                  succeeded: result.succeededCount,
                  failed: result.failedCount,
                  requested: result.requestedCount,
                })}
              </Banner>
            </div>
            <ul className="space-y-1.5">
              {result.results.map((r) => (
                <li
                  key={r.targetId}
                  className="flex items-center justify-between gap-2 rounded-md border border-outline px-2.5 py-1.5"
                  data-testid={`f43-bulk-result-${r.targetId}`}
                >
                  <span className="font-mono text-[11px] text-ink-2 truncate">{r.targetId}</span>
                  <Badge tone={resultTone(r.status)} size="sm">
                    {t(`moderation:bulk.result.${r.status}`)}
                  </Badge>
                </li>
              ))}
            </ul>
            <Button
              variant="secondary"
              size="sm"
              onClick={downloadCsv}
              data-testid="f43-bulk-download-csv"
            >
              {t('moderation:bulk.download_csv')}
            </Button>
          </div>
        )}
      </div>
    </Drawer>
  );
}
